// [PATCH_ROCNI_UVOZ_PARTNER_V1] Skupna maska partnerja s FURS/VIES preverjanjem.
// - PartnerFursForm: vgnezden obrazec (PartnerSelector -> Dokumenti / rocni vnos)
// - PartnerFursModal: modalni ovoj (SifrantiPage "Nov partner / Uredi partnerja")
// Gumb "Preveri" poklice GET /partner-lookup?davcna=... (FURS tabela + VIES)
// in predizpolni prazna polja. Shranjevanje: POST /sifranti/partnerji/{cid}/nov
// ali PUT /sifranti/partnerji/{cid}/{id}. onSaved(partner) vrne shranjen objekt
// (id, name, tax_number, vasco_code, ibani, iban=primarni ...) za izbiro v maski.
import React, { useEffect, useId, useState } from 'react';  // [PATCH partner-stevilcenje v1b]
import { X, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api';

const fieldCls = 'w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500 text-gray-900 dark:text-white';
const DDV_OPTS: { v: string; l: string }[] = [
  { v: '', l: 'Ni podatka' },
  { v: 'ne', l: 'Ne' },
  { v: 'da', l: 'Da' },
  { v: 'eu', l: 'Izvoz ali EU' },
  { v: 'mali', l: 'Mali davčni zavezanec' },
];

export function PartnerFursForm({ companyId, initial, onSaved, onCancel }: {
  companyId: string;
  initial?: any;                 // { id?, name?, tax_number?, ... } — id => urejanje (PUT)
  onSaved: (partner: any) => void;
  onCancel?: () => void;
}) {
  const zac = initial || {};
  const nov = !zac.id;
  const uid = useId();
  const [f, setF] = useState<any>({
    vasco_code: zac.vasco_code || '',
    name: zac.name || '',
    address: zac.address || '',
    postal_code: zac.postal_code || '',
    city: zac.city || '',
    country: zac.country || 'SI',
    tax_number: zac.tax_number || '',
    ddv_status: zac.ddv_status || '',
    vat_number: zac.vat_number || '',
    maticna: zac.registration_number || zac.maticna || '',  // [PATCH partner-maska-furs v2]
    skd: zac.skd || '',
    ibani: Array.isArray(zac.ibani) ? zac.ibani.map((x: any) => ({ iban: x.iban || '', banka: x.banka || '', primarni: !!x.primarni })) : [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  // [PATCH gurs-naslovi v1] samodejno dopolnjevanje naslova / poste / kraja iz GURS Registra naslovov (samo drzava SI)
  const [naslovPredlogi, setNaslovPredlogi] = useState<any[]>([]);
  const [naslovOdprt, setNaslovOdprt] = useState(false);
  const [krajPredlogi, setKrajPredlogi] = useState<any[]>([]);
  const [krajOdprt, setKrajOdprt] = useState(false);
  const gursPreskoci = React.useRef(false);   // po izbiri predloga naslednji useEffect na address preskoci
  const gursZap = React.useRef(0);            // pozen odgovor ne prepise novejsega
  const jeSI = String(f.country || 'SI').trim().toUpperCase() === 'SI';
  useEffect(() => {
    if (!jeSI) return;
    if (gursPreskoci.current) { gursPreskoci.current = false; return; }
    const s = String(f.address || '').trim();
    if (s.length < 3) { setNaslovPredlogi([]); return; }
    const moj = ++gursZap.current;
    const posta = /^\d{4}$/.test(String(f.postal_code || '').trim()) ? String(f.postal_code).trim() : undefined;
    const t = setTimeout(() => {
      api.get('/sifranti/naslovi', { params: { q: s, posta } })
        .then(r => { if (moj === gursZap.current) { setNaslovPredlogi(r.data || []); setNaslovOdprt(true); } })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [f.address]);  // eslint-disable-line
  const izberiNaslov = (p: any) => {
    gursPreskoci.current = true;
    const ulica = p.ulica || p.naselje;
    const naslov = p.hs ? `${ulica} ${p.hs}${p.hs_dodatek || ''}` : `${ulica} `;
    setF((prev: any) => ({ ...prev, address: naslov, postal_code: p.posta_sifra, city: p.posta_naziv }));
    setNaslovOdprt(false); setNaslovPredlogi([]);
  };
  useEffect(() => {
    if (!jeSI) return;
    const p = String(f.postal_code || '').trim();
    if (!/^\d{4}$/.test(p)) return;
    api.get('/sifranti/poste', { params: { q: p } })
      .then(r => { const z = (r.data || []).find((x: any) => x.sifra === p); if (z && z.naziv !== f.city) set('city', z.naziv); })
      .catch(() => {});
  }, [f.postal_code]);  // eslint-disable-line
  useEffect(() => {
    if (!jeSI || !krajOdprt) return;
    const s = String(f.city || '').trim();
    if (s.length < 2) { setKrajPredlogi([]); return; }
    const t = setTimeout(() => { api.get('/sifranti/poste', { params: { q: s } }).then(r => setKrajPredlogi(r.data || [])).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [f.city, krajOdprt]);  // eslint-disable-line
  const gursSeznamCls = 'absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl';
  const gursVrsticaCls = 'w-full text-left px-3 py-1.5 text-sm text-gray-900 dark:text-white hover:bg-blue-50 dark:hover:bg-gray-700';

  const setIban = (idx: number, k: string, v: any) => setF((p: any) => ({ ...p, ibani: p.ibani.map((r: any, i: number) => (i === idx ? { ...r, [k]: v } : r)) }));
  const setPrimary = (idx: number) => setF((p: any) => ({ ...p, ibani: p.ibani.map((r: any, i: number) => ({ ...r, primarni: i === idx })) }));
  const addIban = () => setF((p: any) => ({ ...p, ibani: [...p.ibani, { iban: '', banka: '', primarni: p.ibani.length === 0 }] }));
  const removeIban = (idx: number) => setF((p: any) => {
    const arr = p.ibani.filter((_: any, i: number) => i !== idx);
    if (arr.length && !arr.some((x: any) => x.primarni)) arr[0].primarni = true;
    return { ...p, ibani: arr };
  });

  // FURS/VIES preverjanje davcne in predizpolnjevanje praznih polj
  const [preverjam, setPreverjam] = useState(false);
  const [lookupSporocilo, setLookupSporocilo] = useState<{ tip: 'ok' | 'napaka'; besedilo: string } | null>(null);
  /* [PATCH skd-sifrant v3] */
  const [skdNaziv, setSkdNaziv] = useState<string>(zac.skd_naziv || '');
  /* [PATCH register-panel v5] enoten register objekt (FURS + UJP) */
  const regIz = (src: any) => {
    if (!src) return null;
    const f = src.furs || src;
    const has = f && (f.vrsta || f.datum_ddv || f.oznaka != null || src.skd_naziv || src.je_pu || src.skd || src.skis);  /* [PATCH skis-sektor v7] */
    if (!has) return null;
    return {
      vrsta: f.vrsta || src.vrsta || '',
      zavezanec_ddv: (src.zavezanec_ddv != null ? src.zavezanec_ddv : f.zavezanec_ddv),
      datum_ddv: src.datum_ddv || f.datum_ddv || '',
      oznaka: src.oznaka || f.oznaka || '',
      skd: src.skd || f.skd || '',
      skd_naziv: src.skd_naziv || '',
      je_pu: !!src.je_pu,
      pu_sifra: src.pu_sifra || '',
      pu_tip: src.pu_tip || '',
      skis: src.skis || '',  /* [PATCH skis-sektor v7] */
      skis_naziv: src.skis_naziv || '',
    };
  };
  const [reg, setReg] = useState<any>(regIz(zac));
  /* [PATCH partner-stevilcenje v1] predlog sifre + sledenje davcni */
  const [sifraNacin, setSifraNacin] = useState<string>('davcna');
  const [sifraPredlog, setSifraPredlog] = useState<string>('');  // zadnji server predlog (za "ni rocno spremenjeno")
  const predlagajSifro = async (dav?: string) => {
    if (!nov || !companyId) return;
    try {
      const r = await api.get(`/sifranti/partnerji/${companyId}/naslednja-sifra${dav ? `?davcna=${encodeURIComponent(dav)}` : ''}`);
      setSifraNacin(r.data?.nacin || 'davcna');
      const pred = String(r.data?.sifra || '');
      if (!pred) return;
      // povozi le, ce je polje prazno ALI se enako prejsnjemu predlogu (ni rocnega vnosa)
      setF((p: any) => (!String(p.vasco_code || '').trim() || String(p.vasco_code) === sifraPredlog) ? { ...p, vasco_code: pred } : p);
      setSifraPredlog(pred);
    } catch { /* tiho */ }
  };
  useEffect(() => { predlagajSifro(); /* ob odprtju nove maske */ }, []);  // eslint-disable-line
  /* [PATCH preveri-prepis v6] predlogi prepisa obstojecih polj iz FURS */
  const [predlogi, setPredlogi] = useState<Array<{ polje: string; label: string; staro: string; novo: string; izbran: boolean; iban?: string; banka?: string }> | null>(null);  // [PATCH rtr-spremembe v2] iban/banka za RTR vrstice

  /* [PATCH rtr-iban v1] predlogi TRR iz AJPES RTR registra (samo predlog, nikoli samodejni prepis) */
  const [rtrPredlogi, setRtrPredlogi] = useState<Array<{ iban: string; banka: string; blokiran: boolean; vrsta: string }>>([]);
  const normIban = (s: any) => String(s || '').replace(/\s/g, '').toUpperCase();
  /* [PATCH rtr-spremembe v2] primerjava IBAN na ZADNJIH 15 STEVKAH.
     V bazi sta dve obliki: poln 'SI56xxxxxxxxxxxxxxx' in goli 15-mestni BBAN.
     Brez tega bi bila ista racuna obravnavana kot razlicna. */
  const norm15 = (s: any) => { const d = String(s || '').replace(/\D/g, ''); return d.length >= 15 ? d.slice(-15) : d; };
  /* tuj IBAN = zacne se z dvema crkama, ki nista SI. RTR tujih racunov NE vodi. */
  const jeTujIban = (s: any) => { const t = String(s || '').replace(/\s/g, '').toUpperCase(); return /^[A-Z]{2}/.test(t) && !t.startsWith('SI'); };
  const dodajRtrIban = (x: any) => {
    setF((p: any) => {
      const ze = (p.ibani || []).some((r: any) => normIban(r.iban) === normIban(x.iban));
      if (ze) return p;
      return { ...p, ibani: [...(p.ibani || []), { iban: x.iban, banka: x.banka || '', primarni: (p.ibani || []).length === 0 }] };
    });
    setRtrPredlogi(prev => prev.filter(y => normIban(y.iban) !== normIban(x.iban)));
  };
  const oznakaPolja: Record<string, string> = { name: 'Naziv', address: 'Naslov', postal_code: 'Poštna št.', city: 'Kraj', maticna: 'Matična', skd: 'SKD', vat_number: 'ID za DDV', ddv_status: 'Davčni zavezanec' };
  const uporabiPredloge = () => {
    if (!predlogi) return;
    const izbrani = predlogi.filter(x => x.izbran);
    /* [PATCH rtr-spremembe v2] loci RTR vrstice od navadnih FURS polj */
    const rtrOdstrani = izbrani.filter(x => x.polje === '__rtr_odstrani');
    const rtrDodaj = izbrani.filter(x => x.polje === '__rtr_dodaj');
    const poljaFurs = izbrani.filter(x => !String(x.polje).startsWith('__rtr'));
    setF((p: any) => {
      const n = { ...p };
      poljaFurs.forEach(x => { n[x.polje] = x.novo; });
      if (rtrOdstrani.length || rtrDodaj.length) {
        const ven = new Set(rtrOdstrani.map(x => norm15(x.iban)));
        let arr = (n.ibani || []).filter((r: any) => !ven.has(norm15(r.iban)));
        rtrDodaj.forEach(x => {
          if (!arr.some((r: any) => norm15(r.iban) === norm15(x.iban))) {
            arr = [...arr, { iban: x.iban, banka: x.banka || '', primarni: false }];
          }
        });
        if (arr.length && !arr.some((r: any) => r.primarni)) {
          arr = arr.map((r: any, i: number) => ({ ...r, primarni: i === 0 }));
        }
        n.ibani = arr;
      }
      return n;
    });
    const skdP = poljaFurs.find(x => x.polje === 'skd');
    if (skdP) naloziSkdNaziv(skdP.novo);
    setPredlogi(null);
  };
  const drzavaNaziv = (k: string): string => {
    const M: Record<string, string> = { SI:'Slovenija', AT:'Avstrija', DE:'Nemčija', IT:'Italija', HR:'Hrvaška', HU:'Madžarska', FR:'Francija', BE:'Belgija', NL:'Nizozemska', PL:'Poljska', CZ:'Češka', SK:'Slovaška', ES:'Španija', PT:'Portugalska', SE:'Švedska', DK:'Danska', FI:'Finska', IE:'Irska', GR:'Grčija', RO:'Romunija', BG:'Bolgarija', LU:'Luksemburg', LT:'Litva', LV:'Latvija', EE:'Estonija', CY:'Ciper', MT:'Malta' };
    return M[(k || '').toUpperCase()] || '';
  };
  const naloziSkdNaziv = async (koda: string) => {
    const k = (koda || '').trim();
    if (!/^\d{2}\.\d{3}$/.test(k)) { setSkdNaziv(''); return; }
    try { const r = await api.get(`/sifranti/skd/${k}`); setSkdNaziv(r.data?.naziv || ''); } catch { setSkdNaziv(''); }
  };

  const preveriDavcno = async () => {
    const vnos = (f.tax_number || '').trim();
    if (!vnos) { setLookupSporocilo({ tip: 'napaka', besedilo: 'Vpiši davčno številko.' }); return; }
    setPreverjam(true); setLookupSporocilo(null);
    try {
      const r = await api.get(`/partner-lookup?davcna=${encodeURIComponent(vnos)}`);
      const d = r.data || {};
      if (!d.najden) {
        setLookupSporocilo({ tip: 'napaka', besedilo: d.napaka || 'Ni najdeno.' });
      } else {
        // [PATCH preveri-prepis v6] FURS ciljne vrednosti + razclenjen naslov
        const naslovM = d.naslov ? String(d.naslov).match(/^(.+),\s*(\d{4,5})\s+(.+)$/) : null;
        const fursCilj: Record<string, string> = {};
        if (d.naziv) fursCilj.name = String(d.naziv);
        if (naslovM) { fursCilj.address = naslovM[1].replace(/\s+/g, ' ').trim(); fursCilj.postal_code = naslovM[2]; fursCilj.city = naslovM[3].trim(); }
        else if (d.naslov) { fursCilj.address = String(d.naslov); }
        if (d.maticna) fursCilj.maticna = String(d.maticna);
        if (d.skd) fursCilj.skd = String(d.skd);
        if (d.ddv_id) fursCilj.vat_number = String(d.ddv_id);
        if (d.vir !== 'VIES') fursCilj.ddv_status = d.zavezanec_ddv ? 'da' : 'ne';
        const prazna: Record<string, string> = {};
        const razlike: Array<{ polje: string; label: string; staro: string; novo: string; izbran: boolean }> = [];
        Object.keys(fursCilj).forEach(k => {
          const trenutno = String((f as any)[k] || '').trim();
          const novo = String(fursCilj[k] || '').trim();
          if (!novo) return;
          if (!trenutno) prazna[k] = novo;                    // prazno -> napolni tiho
          else if (trenutno !== novo) razlike.push({ polje: k, label: oznakaPolja[k] || k, staro: trenutno, novo, izbran: true });  // obstaja & drugacno -> predlog
        });
        setF((p: any) => {
          const n = { ...p };
          if (d.davcna) n.tax_number = d.davcna;
          if (d.drzava) n.country = d.drzava === 'EL' ? 'GR' : d.drzava;
          Object.keys(prazna).forEach(k => { n[k] = prazna[k]; });  // samo prazna
          return n;
        });
        if (razlike.length) setPredlogi(razlike); else setPredlogi(null);  // odpri pregled, ce so razlike
        if (nov && d.davcna) predlagajSifro(String(d.davcna));  // [PATCH partner-stevilcenje v1] sifra sledi davcni
        const vir = d.vir === 'VIES' ? 'VIES (EU)' : 'FURS';
        const oznakaTxt = d.oznaka === 'P' ? ' · oznaka P (ni plačnik po 76.a)' : d.oznaka === 'O' ? ' · oznaka O (cestni prevoz)' : '';  // [PATCH partner-maska-furs v2]
        const puTxt = d.je_pu ? ` · proračunski uporabnik${d.pu_sifra ? ' (šifra '+d.pu_sifra+')' : ''}` : '';  // [PATCH pu-znacka v4]
        if (d.skd_naziv) setSkdNaziv(d.skd_naziv); else if (d.skd) naloziSkdNaziv(d.skd);  // [PATCH skd-sifrant v3]
        setReg(regIz(d));  // [PATCH register-panel v5]

        /* [PATCH rtr-spremembe v2] isti gumb "Preveri" uredi tudi TRR iz AJPES RTR:
           A: nov partner brez racunov -> vpisi vse odprte SAMODEJNO
           B: obstojec partner -> sestavi predloge sprememb v isti panel kot FURS */
        try {
          const rr = await api.get(`/sifranti/rtr-iban?davcna=${encodeURIComponent(String(d.davcna || vnos))}`);
          const seznam: any[] = Array.isArray(rr.data?.racuni) ? rr.data.racuni : [];
          const odprti = seznam.filter((x: any) => x && x.iban && x.odprt);
          const obstojeci: any[] = (f.ibani || []).filter((r: any) => String(r.iban || '').trim());
          setRtrPredlogi([]);
          if (obstojeci.length === 0) {
            /* A: NOV partner -> samodejni vpis vseh odprtih racunov */
            if (odprti.length) {
              setF((p: any) => ({ ...p, ibani: odprti.map((x: any, i: number) => ({ iban: x.iban, banka: x.banka || '', primarni: i === 0 })) }));
            }
          } else {
            /* B: OBSTOJEC partner -> predlogi sprememb */
            const rtrPo15 = new Map<string, any>();
            seznam.forEach((x: any) => { if (x && x.iban) rtrPo15.set(norm15(x.iban), x); });
            const vrstice: any[] = [];
            obstojeci.forEach((r: any) => {
              if (jeTujIban(r.iban)) {
                vrstice.push({ polje: '__rtr_odstrani', label: 'TRR ni v AJPES registru (tuj račun — preveri!)',
                               staro: String(r.iban), novo: 'odstrani', izbran: false, iban: String(r.iban) });
                return;
              }
              const najden = rtrPo15.get(norm15(r.iban));
              if (najden && !najden.odprt) {
                vrstice.push({ polje: '__rtr_odstrani', label: 'TRR ZAPRT' + (najden.zaprt_od ? ' ' + najden.zaprt_od : ''),
                               staro: String(r.iban), novo: 'odstrani', izbran: true, iban: String(r.iban) });
              } else if (!najden) {
                vrstice.push({ polje: '__rtr_odstrani', label: 'TRR ni v AJPES registru (preveri!)',
                               staro: String(r.iban), novo: 'odstrani', izbran: false, iban: String(r.iban) });
              }
            });
            const imaPo15 = new Set(obstojeci.filter((r: any) => !jeTujIban(r.iban)).map((r: any) => norm15(r.iban)));
            odprti.forEach((x: any) => {
              if (!imaPo15.has(norm15(x.iban))) {
                vrstice.push({ polje: '__rtr_dodaj', label: 'TRR nov iz AJPES' + (x.banka ? ' — ' + x.banka : ''),
                               staro: '', novo: String(x.iban), izbran: true, iban: String(x.iban), banka: x.banka || '' });
              }
            });
            if (vrstice.length) setPredlogi((ps: any) => ([...(ps || []), ...vrstice]));
          }
        } catch { setRtrPredlogi([]); }
        setLookupSporocilo({ tip: 'ok', besedilo: `Najdeno (${vir}): ${d.naziv || d.ddv_id || d.davcna}` + oznakaTxt + puTxt + (d.opomba ? ` — ${d.opomba}` : '') });
      }
    } catch (e: any) {
      setLookupSporocilo({ tip: 'napaka', besedilo: e?.response?.data?.detail || 'Napaka pri preverjanju.' });
    }
    setPreverjam(false);
  };

  const shrani = async () => {
    if (!f.name.trim()) { toast.error('Naziv je obvezen'); return; }
    const ibani = (f.ibani || [])
      .filter((x: any) => (x.iban || '').trim())
      .map((x: any) => ({ iban: x.iban.trim(), banka: (x.banka || '').trim(), primarni: !!x.primarni }));
    if (ibani.length && !ibani.some((x: any) => x.primarni)) ibani[0].primarni = true;
    // SI partnerji: davcna brez "SI" predpone (konvencija sifranta / RvO)
    let dav = String(f.tax_number || '').trim();
    if ((f.country || 'SI').trim().toUpperCase() === 'SI' && /^SI\d+$/i.test(dav)) {
      dav = dav.replace(/^SI/i, '');
    }
    const body = { ...f, tax_number: dav, ibani };
    setSaving(true);
    try {
      let id = zac.id;
      if (nov) {
        const r = await api.post(`/sifranti/partnerji/${companyId}/nov`, body);
        id = r.data?.id;
      } else {
        await api.put(`/sifranti/partnerji/${companyId}/${zac.id}`, body);
      }
      toast.success(nov ? 'Partner dodan!' : 'Shranjeno!');
      const prim = ibani.find((x: any) => x.primarni)?.iban || ibani[0]?.iban || '';
      onSaved({ id, ...body, iban: prim });
    } catch (e: any) {
      const det = e?.response?.data?.detail || '';
      if (/davčno|davcno|ze obstaja|že obstaja/i.test(det)) { toast.error('Partner s to davčno številko že obstaja v šifrantu.'); setSaving(false); return; }  // [PATCH partner-stevilcenje v1]
      toast.error(e.response?.data?.detail || 'Napaka');
      setSaving(false);
    }
  };

  return (
    <>
      <div className="px-5 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Šifra{/* [PATCH partner-neaktiven-maska v1] */}{zac?.is_active === false && (<span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300" title="Partner je v Opalu/Vascu zaprt — v izbirnikih dokumentov se ne ponuja. Aktivnost se ureja v Opalu/Vascu.">NEAKTIVEN</span>)}</label>
            {/* [PATCH maska-placeholder v3] brez namiga "(Vasco)" — sifra je glede na
                izvoz_program lahko Vasco, Opal ali Panteon */}
            <input value={f.vasco_code} onChange={e => set('vasco_code', e.target.value)} className={fieldCls} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-gray-500 block mb-1">Naziv *</label>
            <input value={f.name} onChange={e => set('name', e.target.value)} className={fieldCls} placeholder="Naziv podjetja" />
          </div>
          <div className="col-span-2 md:col-span-3 relative">
            <label className="text-xs text-gray-500 block mb-1">Naslov</label>{/* [PATCH gurs-naslovi v1] predlogi iz GURS */}
            <input value={f.address} onChange={e => set('address', e.target.value)} autoComplete="off"
              onFocus={() => { if (naslovPredlogi.length) setNaslovOdprt(true); }}
              onBlur={() => setTimeout(() => setNaslovOdprt(false), 150)}
              className={fieldCls} placeholder={jeSI ? "Ulica in hišna številka (predlogi iz registra)" : "Ulica in hišna številka"} />
            {naslovOdprt && naslovPredlogi.length > 0 && (
              <div className={gursSeznamCls}>
                {naslovPredlogi.map((p: any, i: number) => (
                  <button type="button" key={i} onMouseDown={() => izberiNaslov(p)} className={gursVrsticaCls}>
                    {(p.ulica || p.naselje) + (p.hs ? ' ' + p.hs + (p.hs_dodatek || '') : '')}
                    <span className="text-xs text-gray-400 ml-2">{p.ulica ? p.naselje + ' · ' : ''}{p.posta_sifra} {p.posta_naziv}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Poštna št.</label>
            <input value={f.postal_code} onChange={e => set('postal_code', e.target.value)} autoComplete="off" className={fieldCls} placeholder="4248" />{/* [PATCH gurs-naslovi v1] kraj se dopolni iz gurs_poste */}
          </div>
          <div className="relative">
            <label className="text-xs text-gray-500 block mb-1">Kraj</label>{/* [PATCH gurs-naslovi v1] predlogi post */}
            <input value={f.city} onChange={e => { set('city', e.target.value); setKrajOdprt(true); }} autoComplete="off"
              onBlur={() => setTimeout(() => setKrajOdprt(false), 150)} className={fieldCls} placeholder="Lesce" />
            {krajOdprt && krajPredlogi.length > 0 && (
              <div className={gursSeznamCls}>
                {krajPredlogi.map((p: any) => (
                  <button type="button" key={p.sifra} className={gursVrsticaCls}
                    onMouseDown={() => { setF((prev: any) => ({ ...prev, postal_code: p.sifra, city: p.naziv })); setKrajOdprt(false); setKrajPredlogi([]); }}>
                    {p.sifra} {p.naziv}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Država{drzavaNaziv(f.country) ? ` — ${drzavaNaziv(f.country)}` : ''}</label>{/* [PATCH skd-sifrant v3] */}
            <input value={f.country} onChange={e => set('country', e.target.value.toUpperCase())} maxLength={2} className={fieldCls} placeholder="SI" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Davčna številka</label>
            <div className="flex gap-1">
              <input value={f.tax_number} onChange={e => { set('tax_number', e.target.value); if (nov && sifraNacin === 'davcna') { const v = e.target.value.replace(/\D/g, ''); if (v.length >= 1 && v.length <= 8) predlagajSifro(v); } }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); preveriDavcno(); } }}
                className={fieldCls} placeholder="12345678" />
              <button type="button" onClick={preveriDavcno} disabled={preverjam}
                title="Preveri v FURS/VIES in izpolni prazna polja"
                className="px-2.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded-lg whitespace-nowrap">
                {preverjam ? '…' : 'Preveri'}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Davčni zavezanec</label>
            <select value={f.ddv_status} onChange={e => set('ddv_status', e.target.value)} className={fieldCls}>
              {DDV_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>{/* [PATCH partner-maska-furs v2] */}
            <label className="text-xs text-gray-500 block mb-1">Matična številka</label>
            <input value={f.maticna} onChange={e => set('maticna', e.target.value)} className={fieldCls} placeholder="1234567000" />
          </div>
          <div>{/* [PATCH skd-sifrant v3] SKD z zivim opisom iz sifranta */}
            <label className="text-xs text-gray-500 block mb-1">SKD dejavnost</label>
            <input value={f.skd} onChange={e => { set('skd', e.target.value); naloziSkdNaziv(e.target.value); }} className={fieldCls} placeholder="npr. 69.200" />
            {skdNaziv && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{skdNaziv}</p>}
          </div>
          {/* [PATCH register-panel v5] Datum reg. DDV in Oznaka preseljena v spodnji register panel */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Identifikacijska (ID za DDV)</label>
            <input value={f.vat_number} onChange={e => set('vat_number', e.target.value)} className={fieldCls} placeholder="SI12345678" />
          </div>
        </div>

        {lookupSporocilo && (
          <p className={clsx('text-xs', lookupSporocilo.tip === 'ok' ? 'text-green-600 dark:text-green-400' : 'text-red-500')}>
            {lookupSporocilo.besedilo}
          </p>
        )}
        {reg && (  /* [PATCH register-panel v5] read-only podatki iz FURS/UJP */
          <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-3 bg-gray-50/60 dark:bg-gray-900/40">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Iz registra FURS/UJP</span>
              {reg.je_pu && <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-semibold">{reg.pu_tip === 'A' ? 'PU — neposredni' : 'PU — posredni'}{reg.pu_sifra ? ' · ' + reg.pu_sifra : ''}</span>}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
              {reg.vrsta && <div><span className="text-gray-400">Vrsta zavezanca:</span> <span className="text-gray-700 dark:text-gray-200">{reg.vrsta === 'PO' ? 'Pravna oseba' : reg.vrsta === 'DEJ' ? 'Fizična oseba z dejavnostjo' : reg.vrsta === 'FO' ? 'Fizična oseba' : reg.vrsta}</span></div>}
              {(reg.zavezanec_ddv != null) && <div><span className="text-gray-400">DDV zavezanec:</span> <span className={reg.zavezanec_ddv ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>{reg.zavezanec_ddv ? 'Da' : 'Ne'}</span></div>}
              {reg.datum_ddv && <div><span className="text-gray-400">Reg. za DDV od:</span> <span className="text-gray-700 dark:text-gray-200">{reg.datum_ddv}</span></div>}
              {reg.oznaka && <div><span className="text-gray-400">Oznaka 76.a:</span> <span className="text-amber-600 dark:text-amber-400">{reg.oznaka === 'P' ? 'P — ni plačnik' : reg.oznaka === 'O' ? 'O — cestni prevoz' : reg.oznaka}</span></div>}
              {reg.skd && <div className="col-span-2"><span className="text-gray-400">SKD:</span> <span className="text-gray-700 dark:text-gray-200 font-mono">{reg.skd}</span>{reg.skd_naziv ? <span className="text-gray-600 dark:text-gray-300"> — {reg.skd_naziv}</span> : ''}</div>}
              {reg.skis && <div className="col-span-2"><span className="text-gray-400">Sektor (SKIS):</span> <span className="text-gray-700 dark:text-gray-200 font-mono">{reg.skis}</span>{reg.skis_naziv ? <span className="text-gray-600 dark:text-gray-300"> — {reg.skis_naziv}</span> : ''}</div>}{/* [PATCH skis-sektor v7] */}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">Samodejno iz FURS/UJP registra, osveženo dnevno. Podatki niso ročno urejljivi.</p>
          </div>
        )}
        {predlogi && predlogi.length > 0 && (  /* [PATCH preveri-prepis v6] pregled sprememb */
          <div className="border border-blue-300 dark:border-blue-800 rounded-xl p-3 bg-blue-50/60 dark:bg-blue-950/30">
            <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-2">FURS predlaga spremembe obstoječih polj — izberi, kaj prevzeti:</p>
            <div className="space-y-1.5">
              {predlogi.map((x, i) => (
                <label key={x.polje} className="flex items-start gap-2 text-[11px] cursor-pointer">
                  <input type="checkbox" checked={x.izbran} onChange={e => setPredlogi(ps => ps ? ps.map((y, j) => j === i ? { ...y, izbran: e.target.checked } : y) : ps)} className="mt-0.5" />
                  <span className="flex-1">
                    <span className="text-gray-500">{x.label}: </span>
                    <span className="line-through text-red-400">{x.polje === 'ddv_status' ? (x.staro === 'da' ? 'Da' : x.staro === 'ne' ? 'Ne' : x.staro) : x.staro}</span>
                    <span className="text-gray-400"> → </span>
                    <span className="text-green-600 dark:text-green-400 font-medium">{x.polje === 'ddv_status' ? (x.novo === 'da' ? 'Da' : 'Ne') : x.novo}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={uporabiPredloge} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">Prevzemi izbrano</button>
              <button type="button" onClick={() => setPredlogi(null)} className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400">Obdrži svoje</button>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">TRR / IBAN</label>
            <button onClick={addIban} className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-500">
              <Plus className="w-4 h-4" />Dodaj IBAN
            </button>
          </div>
          {/* [PATCH rtr-iban v1] predlogi odprtih TRR iz AJPES registra */}
          {rtrPredlogi.length > 0 && (
            <div className="mb-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <p className="text-[11px] text-blue-700 dark:text-blue-300 mb-1">Iz registra AJPES (RTR) — odprti računi:</p>
              {rtrPredlogi.map((x: any, i: number) => (
                <div key={i} className="flex items-center gap-2 py-0.5">
                  <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{x.iban}</span>
                  {x.banka && <span className="text-[11px] text-gray-500">{x.banka}</span>}
                  {x.blokiran && <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold" title="Evidenca neporavnanih obveznosti (blokada)">blokiran</span>}
                  <button type="button" onClick={() => dodajRtrIban(x)} className="ml-auto px-2 py-0.5 text-[11px] rounded bg-blue-600 hover:bg-blue-700 text-white">Dodaj</button>
                </div>
              ))}
            </div>
          )}
          {f.ibani.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">Ni vnesenih računov.</p>
          ) : (
            <div className="space-y-2">
              {f.ibani.map((row: any, idx: number) => (
                <div key={idx} className="flex items-center gap-2">
                  <input value={row.iban} onChange={e => setIban(idx, 'iban', e.target.value)} placeholder="SI56 0000 0000 0000 000"
                    className="flex-1 px-2 py-1.5 text-xs font-mono bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500 text-gray-900 dark:text-white" />
                  <input value={row.banka} onChange={e => setIban(idx, 'banka', e.target.value)} placeholder="Banka"
                    className="w-28 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500 text-gray-900 dark:text-white" />
                  <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap" title="Primarni IBAN (uporabi se za SEPA/plačila)">
                    <input type="radio" name={'prim_iban_' + uid} checked={!!row.primarni} onChange={() => setPrimary(idx)} className="accent-blue-500" />
                    primarni
                  </label>
                  <button onClick={() => removeIban(idx)} className="p-1 text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-gray-900">
        {onCancel && <button onClick={onCancel} className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm">Prekliči</button>}
        <button onClick={shrani} disabled={saving} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-xl">
          {saving ? 'Shranjujem...' : 'Shrani'}
        </button>
      </div>
    </>
  );
}

export function PartnerFursModal({ companyId, initial, onClose, onSaved }: {
  companyId: string; initial: any; onClose: () => void; onSaved: (p: any) => void;
}) {
  const nov = !initial?.id;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-900 z-10">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{nov ? 'Nov partner' : 'Uredi partnerja'}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <PartnerFursForm companyId={companyId} initial={initial} onSaved={onSaved} onCancel={onClose} />
      </div>
    </div>
  );
}
